import type { CalibratedPoint } from "./tracking.ts";

export const DISCOVERY_GAME_IDS = ["forest-post", "reef-lens", "moon-harp"] as const;
export type DiscoveryGameId = typeof DISCOVERY_GAME_IDS[number];
export type DiscoveryState = {
  phase: "seek" | "return" | "listen" | "rest" | "complete";
  done: number; dwellMs: number; phaseMs: number; motionMs: number;
};
export const DISCOVERY_TOTALS: Record<DiscoveryGameId, number> = { "forest-post": 12, "reef-lens": 8, "moon-harp": 18 };
export const MUSIC_PHRASES = [[0, 1, 2], [2, 0, 1], [1, 2, 0], [0, 2, 1], [1, 0, 2], [2, 1, 0]] as const;
export const STATIONS: CalibratedPoint[] = [{ x: -.58, y: .06 }, { x: 0, y: -.52 }, { x: .58, y: .06 }];
const POST_ORDER = [0, 2, 1, 2, 0, 1, 0, 2, 1, 2, 0, 1];
const REEF_POINTS = [{ x: -.5, y: -.16 }, { x: .5, y: -.16 }, { x: 0, y: -.48 }, { x: -.38, y: .38 }, { x: .38, y: .38 }, { x: -.4, y: -.36 }, { x: .4, y: -.36 }, { x: 0, y: .45 }];
export function initialDiscovery(game: DiscoveryGameId): DiscoveryState {
  return { phase: game === "moon-harp" ? "listen" : "seek", done: 0, dwellMs: 0, phaseMs: 0, motionMs: 0 };
}
export function discoveryStation(game: DiscoveryGameId, state: DiscoveryState): number {
  return game === "moon-harp" ? MUSIC_PHRASES[Math.min(5, Math.floor(state.done / 3))][state.done % 3] : POST_ORDER[Math.min(11, state.done)];
}
export function discoveryTarget(game: DiscoveryGameId, state: DiscoveryState): CalibratedPoint {
  if (state.phase !== "seek") return { x: 0, y: 0 };
  if (game !== "reef-lens") return STATIONS[discoveryStation(game, state)];
  const base = REEF_POINTS[Math.min(7, state.done)];
  return { x: base.x + Math.sin(state.motionMs / 2200) * .045, y: base.y + Math.cos(state.motionMs / 2800) * .03 };
}
export function discoveryDwell(game: DiscoveryGameId, state: DiscoveryState): number {
  return state.phase === "return" ? 650 : game === "reef-lens" ? 1800 : 850;
}
export function advanceDiscovery(game: DiscoveryGameId, state: DiscoveryState, elapsedMs: number, point: CalibratedPoint | null): DiscoveryState {
  if (state.phase === "complete" || !Number.isFinite(elapsedMs) || elapsedMs <= 0) return state;
  const delta = Math.min(100, elapsedMs);
  if (state.phase === "rest" || state.phase === "listen") {
    const phaseMs = state.phaseMs + delta;
    if (phaseMs < (state.phase === "rest" ? 15_000 : 3600)) return { ...state, phaseMs };
    return { ...state, phase: state.phase === "rest" && game === "moon-harp" ? "listen" : "seek", phaseMs: 0 };
  }
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return state.dwellMs ? { ...state, dwellMs: 0 } : state;
  const target = discoveryTarget(game, state);
  const close = Math.hypot(point.x - target.x, point.y - target.y) <= (game === "reef-lens" && state.phase === "seek" ? .16 : .22);
  const next = { ...state, motionMs: state.motionMs + delta, dwellMs: close ? state.dwellMs + delta : 0 };
  if (next.dwellMs < discoveryDwell(game, state)) return next;
  if (state.phase === "seek") return { ...next, phase: "return", dwellMs: 0, phaseMs: 0 };
  const done = state.done + 1;
  const restAt = game === "forest-post" ? [4, 8] : game === "reef-lens" ? [3, 6] : [6, 12];
  return { ...next, done, dwellMs: 0, phaseMs: 0, phase: done === DISCOVERY_TOTALS[game] ? "complete"
    : restAt.includes(done) ? "rest" : game === "moon-harp" && done % 3 === 0 ? "listen" : "seek" };
}
